import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { DialogCloseButton } from '@/components/ui/responsive-dialog-content'
import { Button } from '@/components/ui/button'
import { config } from '@/config'

interface CredentialsDialogProps {
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Backend APP_NAME from .env. Used for modal title; does not change when Admin sets custom name. */
  defaultAppName?: string
}

export function CredentialsDialog({ trigger, open: controlledOpen, onOpenChange: controlledOnOpenChange, defaultAppName }: CredentialsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? controlledOnOpenChange : setInternalOpen

  const developerName = 'Tarık Kavaz'
  const developerWebsite = 'https://tarik.kavaz.org'
  const registeredTo = config.registeredTo || developerName
  const appVersion = config.appVersion || '1.0.0'

  const { t } = useTranslation()
  const defaultTrigger = (
    <Button
      variant="ghost"
      size="sm"
      className="text-foreground hover:bg-muted/80"
      aria-label={t('header.viewAppCredentials')}
    >
      <Info className="w-4 h-4" />
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || defaultTrigger}
        </DialogTrigger>
      )}
      <DialogContent hideClose className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogCloseButton />
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">{defaultAppName ?? config.defaultAppName ?? t('credentials.ticketSystem')}</DialogTitle>
          <DialogDescription className="text-center pt-2">{t('credentials.developedBy')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 items-center pb-4">
          <img
            src="/tarik_kavaz_org.png"
            alt={t('common.developerLogo')}
            className="object-contain w-20 h-20"
          />
          <div className="space-y-2 text-center">
            <p className="text-lg font-semibold">{developerName}</p>
            {developerWebsite && (
              <a
                href={developerWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {developerWebsite}
              </a>
            )}
          </div>
        </div>
        <div className="py-4 space-y-4 border-t">
          <div className="space-y-1 text-center">
            <p className="text-sm font-medium">{t('credentials.registeredTo')}</p>
            <p className="text-sm text-muted-foreground">{registeredTo}</p>
          </div>
          <div className="space-y-1 text-center">
            <p className="text-sm font-medium">{t('common.version')}</p>
            <p className="text-sm text-muted-foreground">{appVersion}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
